import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Award, Building2, ChevronLeft, ChevronRight, MapPin, Users, Tag, Key, LandPlot, Building } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import type { AgencyTier } from '@/components/agencies/AgencyTierBadge';

const PAGE_SIZE = 8;
const AUTO_MS = 6000;

type AgencyCard = {
  id: string;
  name: string;
  logo_url: string | null;
  city: string | null;
  tier: AgencyTier;
  sale_count: number;
  rent_count: number;
  plot_count: number;
  project_count: number;
  staff_count: number;
  total_listings: number;
};

export function TitaniumAgencies() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

  const { data: agencies } = useQuery<AgencyCard[]>({
    queryKey: ['titanium-agencies-v2'],
    queryFn: async () => {
      const { data: ags } = await supabase
        .from('agencies')
        .select('id, name, logo_url, city')
        .eq('is_verified', true)
        .order('created_at', { ascending: false })
        .limit(40);
      const list = ags || [];
      if (!list.length) return [];
      const agencyIds = list.map(a => a.id);

      // Members per agency
      const { data: members } = await supabase
        .from('agent_profiles')
        .select('agency_id, user_id')
        .in('agency_id', agencyIds);
      const memberRows = members || [];
      const userIds = memberRows.map(m => m.user_id);
      const userToAgency: Record<string, string> = {};
      const staffMap: Record<string, number> = {};
      memberRows.forEach((m: any) => {
        userToAgency[m.user_id] = m.agency_id;
        staffMap[m.agency_id] = (staffMap[m.agency_id] || 0) + 1;
      });

      const saleMap: Record<string, number> = {};
      const rentMap: Record<string, number> = {};
      const plotMap: Record<string, number> = {};

      if (userIds.length) {
        const { data: props } = await supabase
          .from('properties')
          .select('user_id, listing_type')
          .in('user_id', userIds)
          .eq('status', 'active')
          .eq('approval_status', 'approved');
        (props || []).forEach((p: any) => {
          const aid = userToAgency[p.user_id];
          if (!aid) return;
          if (p.listing_type === 'rent') rentMap[aid] = (rentMap[aid] || 0) + 1;
          else saleMap[aid] = (saleMap[aid] || 0) + 1;
        });

        const { data: plots } = await supabase
          .from('plots')
          .select('user_id')
          .in('user_id', userIds)
          .eq('status', 'active')
          .eq('approval_status', 'approved');
        (plots || []).forEach((p: any) => {
          const aid = userToAgency[p.user_id];
          if (!aid) return;
          plotMap[aid] = (plotMap[aid] || 0) + 1;
        });
      }

      // Projects via marketing_agency_id
      const projectMap: Record<string, number> = {};
      const { data: projects } = await supabase
        .from('projects')
        .select('marketing_agency_id')
        .in('marketing_agency_id', agencyIds)
        .eq('approval_status', 'approved');
      (projects || []).forEach((p: any) => {
        if (!p.marketing_agency_id) return;
        projectMap[p.marketing_agency_id] = (projectMap[p.marketing_agency_id] || 0) + 1;
      });

      // Tier (used only for ordering)
      const tierMap: Record<string, AgencyTier> = {};
      const { data: tiers } = await supabase
        .from('agency_featured_tier' as any)
        .select('agency_id, tier')
        .in('agency_id', agencyIds);
      (tiers || []).forEach((t: any) => { tierMap[t.agency_id] = t.tier as AgencyTier; });

      const tierRank = (t?: AgencyTier) => t === 'super_hot' ? 3 : t === 'hot' ? 2 : t === 'standard' ? 1 : 0;

      return list
        .map(a => {
          const sale = saleMap[a.id] || 0;
          const rent = rentMap[a.id] || 0;
          const plot = plotMap[a.id] || 0;
          const project = projectMap[a.id] || 0;
          return {
            id: a.id,
            name: a.name,
            logo_url: a.logo_url,
            city: a.city,
            tier: tierMap[a.id] || null,
            sale_count: sale,
            rent_count: rent,
            plot_count: plot,
            project_count: project,
            staff_count: staffMap[a.id] || 0,
            total_listings: sale + rent + plot + project,
          } as AgencyCard;
        })
        .sort((a, b) =>
          tierRank(b.tier) - tierRank(a.tier)
          || (b.total_listings - a.total_listings)
          || a.name.localeCompare(b.name)
        );
    },
  });

  const pages = useMemo(() => {
    if (!agencies?.length) return [] as AgencyCard[][];
    const out: AgencyCard[][] = [];
    for (let i = 0; i < agencies.length; i += PAGE_SIZE) out.push(agencies.slice(i, i + PAGE_SIZE));
    return out;
  }, [agencies]);

  useEffect(() => {
    if (paused || pages.length <= 1) return;
    const t = setInterval(() => setPage(p => (p + 1) % pages.length), AUTO_MS);
    return () => clearInterval(t);
  }, [paused, pages.length]);

  if (!agencies?.length) return null;

  const prev = () => setPage(p => (p - 1 + pages.length) % pages.length);
  const next = () => setPage(p => (p + 1) % pages.length);

  return (
    <section className="bg-background py-6 hidden md:block">
      <div className="container">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl md:text-2xl font-bold">Titanium Agencies</h2>
          </div>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {pages.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous agencies"
                className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/80 backdrop-blur-sm shadow-sm flex items-center justify-center hover:bg-muted"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={next}
                aria-label="Next agencies"
                className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/80 backdrop-blur-sm shadow-sm flex items-center justify-center hover:bg-muted"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${page * 100}%)` }}
            >
              {pages.map((group, gi) => (
                <div key={gi} className="w-full shrink-0 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 grid-rows-2 gap-2.5">
                  {group.map((a) => {
                    const hasAny = a.sale_count + a.rent_count + a.plot_count + a.project_count > 0;
                    return (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/agencies/${a.id}`)}
                      className="group relative h-full flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.35)] overflow-hidden"
                    >
                      {/* Logo */}
                      <div className="h-[80px] w-[80px] shrink-0 rounded-xl bg-muted overflow-hidden">
                        {a.logo_url ? (
                          <img
                            src={optimizedImageUrl(a.logo_url, { width: 192, height: 192 })}
                            alt={a.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center">
                            <Building2 className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Right column */}
                      <div className="min-w-0 flex-1 h-full flex flex-col justify-between py-0.5">
                        <div className="space-y-1">
                          <div className="font-bold text-[13px] uppercase tracking-tight leading-tight line-clamp-2 break-words">{a.name}</div>
                          <div className="flex items-center gap-1 text-[13px] text-primary">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{a.city || 'Ethiopia'}</span>
                          </div>
                        </div>
                        {hasAny ? (
                          <div className="flex items-center gap-2 text-xs flex-wrap">
                            <Pill icon={<Tag className="h-3.5 w-3.5" />} value={a.sale_count} tone="primary" title="For Sale" />
                            <Pill icon={<Key className="h-3.5 w-3.5" />} value={a.rent_count} tone="emerald" title="For Rent" />
                            <Pill icon={<LandPlot className="h-3.5 w-3.5" />} value={a.plot_count} tone="amber" title="Plots" />
                            <Pill icon={<Building className="h-3.5 w-3.5" />} value={a.project_count} tone="violet" title="Projects" />
                            <Pill icon={<Users className="h-3.5 w-3.5" />} value={a.staff_count} tone="slate" title="Staff" />
                          </div>
                        ) : (
                          <div className="text-[13px] text-muted-foreground">New agency</div>
                        )}
                      </div>
                    </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {pages.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                aria-label={`Page ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === page ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

type Tone = 'primary' | 'emerald' | 'amber' | 'violet' | 'slate';
const TONE_CLS: Record<Tone, string> = {
  primary: 'bg-primary/10 text-primary',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  violet: 'bg-violet-100 text-violet-700',
  slate: 'bg-slate-100 text-slate-700',
};

function Pill({ icon, value, tone = 'primary', title }: { icon: React.ReactNode; value: number; tone?: Tone; title: string }) {
  const dim = value === 0;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${dim ? 'bg-muted text-muted-foreground/70' : TONE_CLS[tone]}`}
    >
      {icon}
      <span className="font-semibold tabular-nums">{value}</span>
    </span>
  );
}
